/**
 * LoginForm — React Hook Form + Zod validation.
 *
 * Fields: username/email, password, remember-me.
 * On submit: calls authStore.login(), caller handles redirect.
 */

import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Box,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Link as MuiLink,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

interface LoginFormValues {
  username: string;
  password: string;
  rememberMe?: boolean;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface LoginFormProps {
  onSubmit: (values: { username: string; password: string }) => Promise<void>;
  onForgotPassword?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function LoginForm({ onSubmit, onForgotPassword }: LoginFormProps) {
  const { t } = useTranslation("auth");
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const loginSchema = useMemo(() => z.object({
    username: z.string().min(1, t("usernameRequired")),
    password: z.string().min(1, t("passwordRequired")),
    rememberMe: z.boolean().optional(),
  }), [t]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: "", password: "", rememberMe: false },
  });

  const doSubmit = async (data: LoginFormValues) => {
    setServerError(null);
    try {
      await onSubmit({ username: data.username, password: data.password });
    } catch (err) {
      setServerError(err instanceof Error ? err.message : t("loginFailed"));
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(doSubmit)}
      noValidate
      sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}
    >
      {serverError && (
        <Alert severity="error" onClose={() => setServerError(null)}>
          {serverError}
        </Alert>
      )}

      <TextField
        label={t("usernameLabel")}
        autoComplete="username"
        autoFocus
        fullWidth
        error={!!errors.username}
        helperText={errors.username?.message}
        {...register("username")}
      />

      <TextField
        label={t("passwordLabel")}
        type={showPassword ? "text" : "password"}
        autoComplete="current-password"
        fullWidth
        error={!!errors.password}
        helperText={errors.password?.message}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword((v) => !v)}
                edge="end"
                size="small"
                aria-label={showPassword ? t("hidePassword") : t("showPassword")}
              >
                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        {...register("password")}
      />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <FormControlLabel
          control={<Checkbox size="small" {...register("rememberMe")} />}
          label={t("rememberMe")}
          sx={{ "& .MuiTypography-root": { fontSize: "0.875rem" } }}
        />
        {onForgotPassword && (
          <MuiLink
            component="button"
            type="button"
            variant="body2"
            onClick={onForgotPassword}
            sx={{ fontSize: "0.875rem" }}
          >
            {t("forgotPassword")}
          </MuiLink>
        )}
      </Box>

      <Button
        type="submit"
        variant="contained"
        size="large"
        fullWidth
        disabled={isSubmitting}
        sx={{ mt: 1, py: 1.25 }}
      >
        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : t("loginButton")}
      </Button>
    </Box>
  );
}
